<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { extractHexId } from 'src/components/TemperatureScheme/helpers/temp_scheme_to_ui';

	import { postReq } from '../../../services/http';

	import {
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';

	import {
		DeviceButton,
		DeviceButtonV2,
		DoubleDeviceButton,
		DoubleDeviceButtonV2,
		Text,
	} from '@Shelly/shelly-design-system';
	import {
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';

	export let item: any = {};
	export let withControls = false;
	export let temperature = 0;
	export let dashControlWidget = false;

	let loading = false;

	$: thermostat = $temp_schemes[item.id];
	/**
	 * Prefer the live temp_scheme from the store, falling back to `item`.
	 * Dashboard widget cards (see genThermostatCardData) only carry
	 * { id, position, _type }, so reading state straight from `item` would be
	 * stale/undefined there. In DashListHolder `item` already is the full
	 * scheme, so the fallback preserves the existing behavior.
	 */
	$: isOn = thermostat?._enabled ?? item?._enabled;

	// temp_schemes normalizes id === _id (stores.ts), so item.id is a safe
	// fallback for the API id when the live scheme/_id is unavailable.
	$: itemId = item?.id ?? item?._id;
	$: thermostatId = thermostat?._id ?? itemId;
	$: thermostatRoom = thermostat?._meta?.room ?? item?._meta?.room;
	$: isFahrenheit = thermostat?.isinfh ?? item?.isinfh;
	$: value = withControls ? Number(temperature) : 0;
	$: default_output = thermostat?._meta?.default_output;

	$: relays =
		typeof thermostat === 'object'
			? thermostat?.toheat?.blk.reduce((acc, curr) => {
					// let decId = curr?.r?.split(':')[1] || undefined;
					let dev_id = extractHexId(curr?.r || '');
					if (dev_id) {
						return [
							...acc,
							$devices[dev_id] || {
								id: dev_id,
								deleted: true,
							},
						];
					}
				}, [])
			: [];

	$: relayIds = relays?.map((dev) => dev.id) ?? [];
	$: relayStatusStore = createMemberStatusSnapshot(relayIds);
	$: relayStatus = $relayStatusStore;

	$: turn_devices =
		relays &&
		relays.filter((dev) => {
			const status = relayStatus?.[real_id(dev.id)];
			if (status) {
				if (is_gen_2(dev)) {
					if (
						(default_output === 'normal' &&
							status?.[get_gen_2_control_component_channel_structure(dev)]?.output) ||
						(default_output === 'reversed' &&
							!status?.[get_gen_2_control_component_channel_structure(dev)]?.output)
					) {
						return dev;
					}
				} else {
					if (
						(default_output === 'normal' && status?.relays?.[dev.channel]?.ison) ||
						(default_output === 'reversed' && !status?.relays?.[dev.channel]?.ison)
					) {
						return dev;
					}
				}
			}
		});

	const count = (type = 'up') => {
		value = type === 'up' ? value + 1 : value - 1;

		postReq(`/device/thermostat/control?id=${thermostatId}`, {
			temp: value.toFixed(1),
		}).then((res) => {
			//
		});
	};

	const enableDisableScene = async (data) => {
		try {
			return await postReq('/scene/enable', data);
		} catch (error) {
			console.error('There is erron on enable/disable scene id ', data.id, error);
			return { isok: false };
		}
	};

	const updateStores = (scene_script) => {
		if (scene_script !== undefined && scene_script._id !== undefined) {
			scenes.editScene(scene_script);
			/** devices statuses - update it to avoid a delay via socket update*/
			let copy_existing_scheme = { ...readDeviceStatus(scene_script._id) };

			if (copy_existing_scheme?.thermostat || copy_existing_scheme.thermostat._enabled) {
				copy_existing_scheme.thermostat._enabled = scene_script._enabled;
				devices_status.update_device_status({
					id: scene_script._id,
					msg: copy_existing_scheme,
				});
			}
		}
	};

	const handleEnableBtn = async (e) => {
		/** get all other temp schemes
		 *  (all because we don't know on other instance of app what it it,
		 *  so disable all other every time)
		 **/
		loading = true;
		let other_in_the_room = Object.keys($temp_schemes).filter(
			(_k) =>
				Number($temp_schemes[_k]._id) !== Number(thermostatId) &&
				Number($temp_schemes[_k]._meta.room) === Number(thermostatRoom),
		);

		if (other_in_the_room.length > 0) {
			let res = await postReq('/scene/bulk_enable', {
				ids: JSON.stringify(other_in_the_room),
				enabled: false,
			});
			if (res.isok) {
				for (let _id of res.data.scene_ids) {
					updateStores(res.data.scene_script[_id]);
				}
			}
		}
		let res = await enableDisableScene({ id: thermostatId, enabled: !isOn });
		if (res?.data?.isok) {
			updateStores(res.data.scene_script);
		}
		loading = false;
	};

	const controlDevices = async () => {
		await Promise.allSettled(
			turn_devices.map((device) => {
				postReq(`/device/${device.category}/control`, {
					id: real_id(device.id),
					channel: device.channel,
					turn: default_output === 'normal' ? 'off' : 'on',
				});
			}),
		)
			.then((res) => console.log(res))
			.catch((err) => console.log(err));
	};
	const a11yProps = {
		ariaLabel: isOn
			? $format('label_turn_off', { default: 'Turn off' })
			: $format('label_turn_on', { default: 'Turn on' }),
		timerAriaLabel: null,
		role: 'switch',
		'aria-checked': isOn,
		'aria-live': 'polite',
	};
</script>

{#if withControls}
	{#if dashControlWidget}
		<DoubleDeviceButtonV2
			id="doubledevicebutton-qn33pjy"
			aria-label={$format('label_thermostat_controls', {
				default: 'Thermostat controls',
			})}
			class="gap-2"
			size="s"
		>
			<DeviceButtonV2
				id="devicebutton-6tgzfni"
				aria-label={$format('button_decrease', { default: 'Button decrease' })}
				slot="buttonLeft"
				icon="fas fa-minus"
				variant="primary"
				on:click={() => count('down')}
				size="s"
			/>
			<svelte:fragment slot="content">
				<Text styleType="controls-large" class="text-bold">{value}</Text>
				<Text styleType="controls-small">{isFahrenheit ? '°F' : '°C'}</Text>
			</svelte:fragment>
			<DeviceButtonV2
				id="devicebutton-nv27qrd"
				aria-label={$format('button_increase', { default: 'Button increase' })}
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={() => count('up')}
				size="s"
			/>
		</DoubleDeviceButtonV2>
	{:else}
		<DoubleDeviceButton
			id="doubledevicebutton-qn33pjy"
			aria-label={$format('label_thermostat_controls', {
				default: 'Thermostat controls',
			})}
			class="gap-2"
		>
			<DeviceButton
				id="devicebutton-6tgzfni"
				aria-label={$format('button_decrease', { default: 'Button decrease' })}
				slot="buttonLeft"
				icon="fas fa-minus"
				variant="primary"
				on:click={() => count('down')}
			/>
			<svelte:fragment slot="content">
				<Text styleType="controls-large" class="text-bold">{value}</Text>
				<Text styleType="controls-small">{isFahrenheit ? '°F' : '°C'}</Text>
			</svelte:fragment>
			<DeviceButton
				id="devicebutton-nv27qrd"
				aria-label={$format('button_increase', { default: 'Button increase' })}
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={() => count('up')}
			/>
		</DoubleDeviceButton>
	{/if}
{:else if dashControlWidget}
	<DeviceButtonV2
		id="devicebutton-xonyohj"
		aria-label={$format('label_device_button', { default: 'Device button' })}
		{a11yProps}
		indicator={thermostat?._meta?.type === 'cool' ? 'on' : 'off'}
		indicatorVariantOn="--color-primary"
		icon="fas fa-power-off"
		variant="primary"
		isActive={isOn}
		isLoading={loading}
		size="s"
		on:click={async (e) => {
			e.stopPropagation();
			await controlDevices();
			await handleEnableBtn(itemId);
		}}
	/>
{:else}
	<DeviceButton
		id="devicebutton-xonyohj"
		aria-label={$format('label_device_button', { default: 'Device button' })}
		{a11yProps}
		indicator={thermostat?._meta?.type === 'cool' ? 'on' : 'off'}
		indicatorVariantOn="--color-primary"
		icon="fas fa-power-off"
		variant="primary"
		isActive={isOn}
		isLoading={loading}
		on:click={async (e) => {
			e.stopPropagation();
			await controlDevices();
			await handleEnableBtn(itemId);
		}}
	/>
{/if}
